import { PageMeta } from "@/components/PageMeta";
import { PolicyLayout, type PolicySection } from "@/components/PolicyLayout";
import { siteConfig } from "@/config/site";
import { routeSeo } from "@/content/seo";

const sections: PolicySection[] = [
  { id: "scope", label: "1. Scope" },
  { id: "information-you-provide", label: "2. Information you provide" },
  { id: "contact-form-data", label: "3. Contact form information" },
  { id: "technical-data", label: "4. Technical and usage data" },
  { id: "cookies", label: "5. Cookies and local storage" },
  { id: "purposes", label: "6. How Devory uses information" },
  { id: "legal-bases", label: "7. Legal bases for processing" },
  { id: "service-providers", label: "8. Service providers" },
  { id: "payment-processing", label: "9. Payment processing" },
  { id: "transfers", label: "10. International transfers" },
  { id: "retention", label: "11. Retention" },
  { id: "security", label: "12. Security" },
  { id: "rights", label: "13. Your rights" },
  { id: "children", label: "14. Children's privacy" },
  { id: "changes", label: "15. Changes to this policy" },
  { id: "contact", label: "16. Contact" },
];

export function PrivacyPage() {
  const seo = routeSeo["/legal/privacy"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/legal/privacy" />
      <PolicyLayout
        slug="privacy"
        title="Privacy Policy"
        intro="This policy explains what information Devory collects when you visit this website, create a Devory Cloud account, or contact Devory, and how that information is used."
        sections={sections}
      >
        <h2 id="scope">1. Scope</h2>
        <p>
          The data controller for information covered by this policy is {siteConfig.legalEntity.name},
          a {siteConfig.legalEntity.structure.toLowerCase()} based in {siteConfig.legalEntity.jurisdiction},{" "}
          trading as {siteConfig.legalEntity.tradingAs} and {siteConfig.legalDeveloper}. This policy covers
          the public Devory website and Devory Cloud (app.devory.app), including account, organisation
          and billing data. It will be further extended to cover the Devory Desktop application as
          its own Cloud connectivity develops, and this page will be updated accordingly.
        </p>

        <h2 id="information-you-provide">2. Information you provide</h2>
        <p>
          When you create a Devory Cloud account, Devory collects your email address and a password
          (stored only as a salted cryptographic hash -- Devory never stores or can recover your
          plain-text password), plus any display name or organisation details you add. Submitting
          the contact form or emailing Devory separately collects your name, email address, optional
          organisation name, enquiry category and message.
        </p>

        <h2 id="contact-form-data">3. Contact form information</h2>
        <p>
          The contact form collects your name, email address, optional organisation name, the
          category of your enquiry, and your message. This information is used only to respond to
          your enquiry and is not used for marketing without your separate consent.
        </p>

        <h2 id="technical-data">4. Technical and usage data</h2>
        <p>
          Cloudflare, which hosts this website and Devory Cloud's backend infrastructure (Workers
          and D1 database), processes standard connection information (such as IP address, request
          headers, and timestamps) as part of delivering and securing both. Session activity (sign-in
          times, active sessions, API usage) is recorded to operate and secure your Devory Cloud
          account. Devory's application code does not run separate advertising or tracking scripts.
        </p>

        <h2 id="cookies">5. Cookies and local storage</h2>
        <p>
          See the <a href="/legal/cookies">Cookie Policy</a> for full detail. In summary, this
          website does not currently set non-essential cookies or load optional analytics scripts.
        </p>

        <h2 id="purposes">6. How Devory uses information</h2>
        <p>
          Devory uses the information described above to respond to enquiries, create and secure
          your account, provide the Devory Cloud platform (organisations, workspaces, projects and
          cloud workflow execution), process payments for paid subscriptions, and operate and secure
          the website.
        </p>

        <h2 id="legal-bases">7. Legal bases for processing</h2>
        <p>
          Where applicable data protection law requires a legal basis, Devory relies on: your
          consent (for example, submitting the contact form), performance of a contract with you
          (creating your account and providing paid or free-tier Devory Cloud service), and Devory's
          legitimate interest in operating and securing the website and platform.
        </p>

        <h2 id="service-providers">8. Service providers</h2>
        <p>
          Devory uses Cloudflare to host this website and Devory Cloud, and to provide security,
          database and abuse-prevention infrastructure. Devory uses Stripe to process subscription
          payments, and Resend to send transactional account email (such as verification and
          password-reset messages). Devory does not use a separate advertising or analytics
          provider. This section will be updated if additional providers are integrated.
        </p>

        <h2 id="payment-processing">9. Payment processing</h2>
        <p>
          Devory uses Stripe to process subscription payments for Devory Cloud. Devory does not
          receive or store your full card details -- Stripe collects and processes payment
          information directly, and Stripe's own privacy practices apply to that data. Devory
          retains billing records (such as plan, subscription status and invoice history) associated
          with your account.
        </p>

        <h2 id="transfers">10. International transfers</h2>
        <p>
          Because this website and Devory Cloud are served through Cloudflare's global network, and
          Devory uses Stripe and Resend as service providers, your data may be handled by
          infrastructure located outside your own country. {siteConfig.legalEntity.name} is
          based in {siteConfig.legalEntity.jurisdiction} (see the Terms of Service, section 17,
          for governing law and jurisdiction). Each of these providers maintains its own safeguards
          for international data transfers under applicable data protection law.
        </p>

        <h2 id="retention">11. Retention</h2>
        <p>
          Contact form submissions are retained only as long as needed to respond to and resolve
          your enquiry, and are not kept indefinitely as a matter of policy. Account and billing data
          is retained for as long as your account is active, plus a reasonable period afterward for
          legal, tax and dispute-resolution purposes; contact Devory to request deletion of your
          account and associated data, subject to those retention obligations.
        </p>

        <h2 id="security">12. Security</h2>
        <p>
          See the <a href="/security">security page</a> for detail on Devory's security posture. In
          summary: access to systems is deny-by-default, and no secrets are embedded in this
          website's client-facing code.
        </p>

        <h2 id="rights">13. Your rights</h2>
        <p>
          Depending on your location, you may have rights to access, correct, or delete personal
          information Devory holds about you, or to object to certain processing. To exercise these
          rights, contact{" "}
          <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>.
        </p>

        <h2 id="children">14. Children's privacy</h2>
        <p>
          The Service is not directed at children, and Devory does not knowingly collect personal
          information from children.
        </p>

        <h2 id="changes">15. Changes to this policy</h2>
        <p>
          Devory may update this policy as the product and its data practices develop. Material
          changes will update the "Last updated" date above.
        </p>

        <h2 id="contact">16. Contact</h2>
        <p>
          For privacy questions or requests, email{" "}
          <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a> or use the{" "}
          <a href="/contact">contact form</a> and select the Privacy category.
        </p>
      </PolicyLayout>
    </>
  );
}
